import { useState, useEffect } from 'react';
import {
  ClickOutsideDetector,
  InputText,
  IconUp,
  IconDown,
  Checkbox,
  ButtonPrimary,
  ButtonSize,
  SelectItemProps,
} from '@ui/src';
import Highlight from '@ui/src/components/autocomplete/highlight';
import entityTypes from './variables';
import { EntitySelectorTypes } from './types';

import cn from './styles.module.css';

const EntitySelector = ({ onChange, selectedEntity }: EntitySelectorTypes) => {
  const [listVisible, setListVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
  const [entityTypesList, setEntityTypesList] = useState<SelectItemProps[]>([]);

  const onSearch = (text: string) => {
    setSearchText(text);

    const entityTypesFormatted = entityTypes.map((item) => ({
      name: item
        .split('_')
        .map((value) => `${value.charAt(0)}${value.slice(1).toLowerCase()}`)
        .join(' '),
      value: item,
    }));

    if (text) {
      setEntityTypesList(
        entityTypesFormatted?.filter((item) => {
          const itemName = item.name.replace('Issue', 'Task');

          return itemName.toLowerCase().includes(text.toLowerCase());
        }),
      );
    } else {
      setEntityTypesList(entityTypesFormatted);
    }
  };

  useEffect(() => {
    setEntityTypesList(
      entityTypes.map((item) => ({
        name: item
          .split('_')
          .map((value) => `${value.charAt(0)}${value.slice(1).toLowerCase()}`)
          .join(' '),
        value: item,
      })),
    );
  }, [entityTypes]);

  useEffect(() => {
    if (selectedEntity) {
      setSelectedEntities(selectedEntity);
    }
  }, [selectedEntity]);

  return (
    <div className={cn.entitySelectorWrapper}>
      <ClickOutsideDetector setOptionsListVisible={setListVisible}>
        <>
          <button
            type="button"
            className={`${cn.showListButton} ${listVisible ? cn.active : ''}`}
            onClick={() => setListVisible(!listVisible)}
          >
            Entity
            {listVisible ? <IconUp className={cn.iconUp} /> : <IconDown />}
          </button>
          {listVisible && (
            <div className={cn.entityList}>
              <div className={cn.entityListTop}>
                <InputText
                  name="searchText"
                  value={searchText}
                  placeholder="Entity name"
                  onChange={(e) => onSearch(e.target.value)}
                />
              </div>
              <div className={cn.entityListOptions}>
                {entityTypesList?.map((item) => (
                  <div className={cn.entityListOption} key={item.value}>
                    <Checkbox
                      name={`checkbox-${item.value}`}
                      type="checkbox"
                      labelClassName={cn.entityCheckbox}
                      checked={selectedEntities.includes(item.value as string)}
                      title=" "
                      onChange={(e) => {
                        let nextList = ([] as string[]).concat(
                          selectedEntities,
                        );

                        if (e.target.checked) {
                          nextList.push(item.value as string);
                        } else {
                          nextList = nextList.filter(
                            (entity) => entity !== item.value,
                          );
                        }

                        setSelectedEntities(nextList);
                      }}
                    />
                    <div className={cn.checkboxHighlight}>
                      <Highlight
                        searchQuery={searchText}
                        text={item.name.replace('Issue', 'Task')}
                        className={cn.entityHighlighted}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className={cn.entityListControl}>
                <ButtonPrimary
                  text="Apply"
                  size={ButtonSize.SMALL}
                  onClick={() => {
                    onChange(selectedEntities);
                    setListVisible(false);
                    setSearchText('');
                    setEntityTypesList(
                      entityTypes.map((item) => ({
                        name: item
                          .split('_')
                          .map(
                            (value) =>
                              `${value.charAt(0)}${value
                                .slice(1)
                                .toLowerCase()}`,
                          )
                          .join(' '),
                        value: item,
                      })),
                    );
                  }}
                />
              </div>
            </div>
          )}
        </>
      </ClickOutsideDetector>
    </div>
  );
};

export default EntitySelector;
